import { Box, Stack } from '@mui/material';
import Modal from '@mui/material/Modal';
import { bulkActions } from '@common-reducers/BulkActionsReducer';
import { DBKanbanThunks } from "@common-reducers/DBServiceThunks";
import { setExtraDataOfTaskId } from '@common-reducers/ExtraDataReducer';
import { taskActions } from '@common-reducers/TasksReducer';
import { NullableId } from "@common/types/interface";
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';

import React, { useState } from "react";
import { DragDropContext, Draggable, DropResult, Droppable, ResponderProvided } from 'react-beautiful-dnd';
import KanbanCard from './kanban-card';
import KanbanColumn from './kanban-column';
import { kanbanColumnOptionsSorted } from '@common-reducers/TaskSelectors';
import { updateTaskOrderValueThunk } from '@common-reducers/TaskThunks';

type Props = {
    kanbanId: NullableId;
}

export default function Kanban({ kanbanId }: Props) {
    const dispatch = useAppDispatch()
    const columnOptions = useAppSelector((state) => kanbanColumnOptionsSorted(state, kanbanId))
    const [open, setOpen] = useState(false);
    const setKanbanCard = (_event: React.MouseEvent<Element>, taskId: string | undefined) => {
        dispatch(taskActions.setTaskViewSideDrawerOpenWith(""));
        dispatch(taskActions.toggleTaskModal(true));

        // set taskId for extra data
        if (taskId) dispatch(setExtraDataOfTaskId(taskId));
    }

    const handleOnDragEnd = (result: DropResult, _provided: ResponderProvided) => {

        if (!result.destination) {
            return;
        }

        if (result.source.droppableId === "label") {

            const [_, optionId] = result.draggableId.split("option_List_")
            const option = columnOptions.find((option) => {
                return option._id === optionId
            })
            if (!option) return;
            const finalOrder = [...columnOptions.filter(o => o._id && o._id !== optionId)]

            finalOrder.splice(result.destination.index, 0, option);

            let columnsOrder: Record<string, number> = {}
            const newColumnsOrder = finalOrder.map((columnOption, columnOptionListIndex) => ({
                id: columnOption._id, number: columnOptionListIndex
            }))
            for (let i in newColumnsOrder) {
                const entry = newColumnsOrder[i];
                if (entry.id) {
                    columnsOrder = {
                        ...columnsOrder,
                        [entry.id]: entry.number
                    }
                }
            }

            dispatch(
                DBKanbanThunks.patch({
                    entity: {
                        _id: kanbanId ?? '',
                        columnsOrder: columnsOrder,
                    }
                })
            )

        }
        else {
            const [___, option_value] = result.destination.droppableId.split("option_list_")
            const [_, taskId] = result.draggableId.split('task_')
            const option = columnOptions.find((option) => {
                return option._id === option_value
            })
            if (!option) return;
            const valueCheck = (option.isDefaultLabel === true)

            dispatch(updateTaskOrderValueThunk({
                taskId: taskId,
                optionId: option_value,
                destinationIndex: result.destination.index,
                isDefault: valueCheck,
                kanbanId,
            }))

        }
    }


    return (
        <>
            <Box sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                zIndex: open ? 9 : -9,
            }} />
            <DragDropContext onDragEnd={(result: DropResult, provided: ResponderProvided) => handleOnDragEnd(result, provided)}>

                <Droppable droppableId='label' direction="horizontal">
                    {(provided) => (
                        <Box {...provided.droppableProps} ref={provided.innerRef} sx={{
                            p: 5,
                        }}>

                            <Stack direction="row" spacing={2} sx={{
                                height: '68vh',
                                overflow: 'auto',
                            }}>
                                {
                                    columnOptions
                                        .map((option, index) => {
                                            return (
                                                <Draggable
                                                    key={`${option._id}`}
                                                    draggableId={`option_List_${option._id}`}
                                                    index={index}
                                                    isDragDisabled={!option._id}>
                                                    {(provided, snapshot) => (
                                                        <KanbanColumn
                                                            snapshot={snapshot}
                                                            provided={provided}
                                                            option={option}
                                                            kanbanId={kanbanId}
                                                            setKanbanCard={setKanbanCard}
                                                            key={index}
                                                        />
                                                    )
                                                    }
                                                </Draggable>
                                            )
                                        })}
                                {provided.placeholder}
                            </Stack>
                        </Box>
                    )}
                </Droppable>
            </DragDropContext >

            <Modal
                open={open}
                onClose={() => {

                    setOpen(false)
                    dispatch(bulkActions.setIsBulkActionsDialogOpen(false))
                    dispatch(bulkActions.toggleMoveTo(false))
                    dispatch(bulkActions.setMovingTo(""))
                    dispatch(bulkActions.setBulActionsMoveToId(''))
                    taskActions.toggleTaskMenu('')
                }}
                sx={{
                    "maxWidth": "1000px",
                    "minWidth": "800px",
                    "margin": "auto",
                    "display": "flex",
                    "alignItems": "center",
                    "justifyContent": "center",
                }}
            >
                <div><KanbanCard /></div>
            </Modal>
        </>
    )
}
